import moment from 'moment'
import { get } from 'lodash'
import configment from '@adc/configment'
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'

const constructPageTitle = event => {
  let prefix = ''

  if (event.trustee_sale) {
    prefix = 'Foreclosure/Trustee Auction'
  } else if (!event.event_configuration?.present_as_online_event) {
    prefix = 'Residential Auction'
  } else {
    prefix = 'Residential Online Auction'
  }

  return `${prefix} - ${event.event_code}`
}

const fetchMorpheusEvent = variables =>
  fetch
    .forAuctionGraph({
      query: gql`
        query resiEvent_fetchEventDetails(
          $eventCode: ID!
          $dslAggregation: String
          $dslVenues: String
        ) {
          seekAggregation(
            type: VENUES
            fields: ["venue_schedule.bid_start_local_date"]
            dsl: $dslAggregation
          )

          seek(type: VENUES, dsl: $dslVenues) {
            content {
              ... on Venue {
                venue_id
              }
            }
          }

          event(eventCode: $eventCode) {
            event_id
            event_code
            title
            trustee_sale
            event_type
            event_configuration {
              present_as_online_event
            }
            venues {
              venue_id
              venue_title
              venue_name
              venue_url
              venue_address {
                street_number
                street_name
                municipality
                country_primary_subdivision
                postal_code
              }
              venue_schedule {
                bid_start_local_date
                bid_start_local_time
                initial_bidding_end_local_date
                initial_bidding_end_local_time
                registration_time
              }

              live_auction {
                location {
                  lat
                  lon
                }
                status
              }
            }
          }
        }
      `,
      variables,
    })
    .then(({ body }) => {
      if (body.errors) {
        throw new Error(`No event with code ${variables.eventCode} found`)
      }

      return body.data
    })

const EventsProvider = {
  fetchEventDetails: async function (req, eventCode) {
    // TODO: if no event throw an error so the server can instrument. I have no idea how often this happens...
    const {
      event: morpheusEvent,
      seekAggregation,
      seek,
    } = await fetchMorpheusEvent({
      eventCode,
      dslAggregation: JSON.stringify({
        and: {
          expressions: [
            {
              equals: {
                fields: ['event.event_code.keyword'],
                values: [eventCode],
              },
            },
            {
              equals: {
                fields: ['event.event_configuration.web_live_status'],
                values: true,
              },
            },
            {
              range: {
                field: 'active_listing_count',
                gte: 1,
              },
            },
          ],
        },
      }),
      dslVenues: JSON.stringify({
        query: {
          and: {
            expressions: [
              {
                equals: {
                  fields: ['event.event_code.keyword'],
                  values: [eventCode],
                },
              },
              {
                equals: {
                  fields: ['event.event_configuration.web_live_status'],
                  values: true,
                },
              },
              {
                range: {
                  field: 'active_listing_count',
                  gte: 1,
                },
              },
            ],
          },
        },
      }),
    })

    const seekAggregationDates =
      seekAggregation &&
      Boolean(seekAggregation['venue_schedule.bid_start_local_date'].length) &&
      seekAggregation['venue_schedule.bid_start_local_date']

    if (morpheusEvent.venues && seek.content) {
      morpheusEvent.venues = morpheusEvent.venues.filter(venue => {
        return seek.content.some(obj => {
          return obj.venue_id === venue.venue_id
        })
      })
    }

    morpheusEvent.__computed__ = {
      page_title: constructPageTitle(morpheusEvent),
      venue_dates: (
        seekAggregationDates ||
        Array.from(
          (morpheusEvent.venues || [])
            .map(venue => venue.venue_schedule.bid_start_local_date)
            .reduce((acc, date) => acc.add(date), new Set())
        )
      )
        .map(date => moment(date.split('T')[0]).format(moment.HTML5_FMT.DATE))
        .sort(),
    }

    morpheusEvent.venues = morpheusEvent.venues?.map(venue => {
      const address = venue.venue_address || {}

      // NOTE: We are fine with the street number being optional because the street name is close enough, but the
      // converse is not true. Something like `12, Irvine` is too generic
      const streetAddress = address.street_name
        ? [address.street_number, address.street_name].filter(Boolean).join(' ')
        : null

      const computedAddress = [
        streetAddress,
        address.municipality,
        address.country_primary_subdivision,
        address.postal_code,
      ]
        .filter(Boolean)
        .join(',')

      return {
        ...venue,
        __computed__: {
          // NOTE: For years we only showed the street name here which lead often to ambiguous results when we linked
          // with a map because the same street appears in different states (and often multiple times within the same
          // state). We know attempt to link to the exact full address.
          address: computedAddress,
        },
      }
    })

    const venueID = get(req, 'query.venueID')
    const venueByDate = get(req, 'query.venueByDate')

    return {
      ...morpheusEvent,
      query: {
        venue_by_date: venueByDate,
        selected_venue_id: venueID,
      },
    }
  },

  // This function is async because it must return a promise.
  fetchAuctionInventory: async function (eventCode, includeCreditBidRevealed) {
    const base = `${configment.get(
      'RESI_GRAPH_API_URL'
    )}/download/adc:event_inventory_report:${eventCode}`
    // Forward the flag only when on, so the URL is unchanged when off.
    const url = includeCreditBidRevealed
      ? `${base}?includeCreditBidRevealed=true`
      : base
    const link = document.createElement('a')
    try {
      const today = new Date().toISOString()
      const filename = `${eventCode}_${today}.xlsx`

      link.href = url
      link.download = filename

      // Required for Firefox in some cases
      document.body.appendChild(link)
      link.click()
    } catch (e) {
      // Fallback for mobile WebViews or browsers that block programmatic download
      window.open(url, '_blank')
    } finally {
      if (link.parentNode) {
        document.body.removeChild(link)
      }
    }
  },
}

export default EventsProvider
